import { TFunction } from 'i18next';
import { SiteArea } from 'graphql-common';
import { getAuthorizationReasonsFor } from '@lib/utils/authorizationReason';

type Args = {
  blocked: SiteArea[];
  destroyedCount: number;
  t: TFunction<'translation', undefined>;
};

/**
 * `siteAreaBulkDestroy` deletes everything it is allowed to and reports the
 * rest, so a partial run must not be announced as a plain success. Turns the
 * `destroyedCount` / `blocked` pair into the message the user actually sees.
 */
export default function getSiteAreasDestroyMessage({
  blocked,
  destroyedCount,
  t,
}: Args): string {
  // The backend reports one reason per area at a time and blocked areas
  // usually trip the same rule, so the de-duplicated set stays short.
  const reasons = blocked.length
    ? getAuthorizationReasonsFor(
        blocked.map(({ canDestroy }) => canDestroy),
      ).join(' ') || t('site-area-disabled-destroy-desc')
    : '';

  // The mutation payload is nullable, so a response may carry neither a
  // deletion nor a blocked area — that is a no-op, never a success.
  if (destroyedCount <= 0) {
    return t('destroy-site-areas-blocked', {
      reasons: reasons || t('something-went-wrong'),
    });
  }

  if (!blocked.length) {
    return destroyedCount > 1
      ? t('destroy-site-areas-success', { destroyedCount })
      : t('destroy-site-area-success');
  }

  return t('destroy-site-areas-partial', {
    blockedCount: blocked.length,
    destroyedCount,
    reasons,
  });
}
